import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import TablePagination from "./TablePagination";
import { getManagers, toggleManagerStatus, updateManager } from "../Apis/ManagerAPIs";
import { getImageUrl } from "../Apis/api";
import usePermissions from "../hook/userPermission";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";

const EmployeeListLayer = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [activeManagersList, setActiveManagersList] = useState([]);
  const [selectedHandoverManager, setSelectedHandoverManager] = useState(null);
  const [handoverLoading, setHandoverLoading] = useState(false);

  const getGroupsOfManager = (manager) => {
    if (!manager) return [];
    const raw = manager.groupIds || manager.groupId || manager.group || [];
    if (Array.isArray(raw)) {
      return raw.map(g => typeof g === "object" ? (g._id || g.id) : g).filter(Boolean);
    }
    if (raw) {
      const id = typeof raw === "object" ? (raw._id || raw.id) : raw;
      return id ? [id] : [];
    }
    return [];
  };

  const fetchManagers = async () => {
    setLoading(true);
    const res = await getManagers({
      page: currentPage,
      limit: rowsPerPage,
      search: debouncedSearchTerm
    });
    if (res.status) {
      const data = res.response?.data || res.response;
      setUsers(Array.isArray(data) ? data : []);
      setTotalRecords(res.response?.total || (Array.isArray(data) ? data.length : 0));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchManagers();
  }, [currentPage, rowsPerPage, debouncedSearchTerm]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value));
    setCurrentPage(0);
  };

  const confirmDelete = async (user) => {
    setUserToDelete(user);
    setSelectedHandoverManager(null);
    const currentStatus = user.isActive ?? user.status;
    const managerGroups = getGroupsOfManager(user);

    if (currentStatus && managerGroups.length > 0) {
      setHandoverLoading(true);
      setShowDeleteModal(true);
      const res = await getManagers({ limit: 1000 });
      if (res.status) {
        const allManagers = res.response?.data || res.response || [];
        const otherActive = allManagers.filter(
          (m) =>
            (m.isActive ?? m.status) === true &&
            (m._id || m.id) !== (user._id || user.id)
        );
        setActiveManagersList(otherActive);
      }
      setHandoverLoading(false);
    } else {
      setShowDeleteModal(true);
    }
  };

  const handleDelete = async () => {
    if (userToDelete) {
      const currentStatus = userToDelete.isActive ?? userToDelete.status;
      const managerAGroups = getGroupsOfManager(userToDelete);

      // If we are making the manager inactive and they have groups
      if (currentStatus && managerAGroups.length > 0) {
        if (!selectedHandoverManager) {
          alert("Please select an active manager to handover the groups.");
          return;
        }

        setLoading(true);

        // Send a single API payload containing isActive: false and reassignManagerId
        const payload = {
          isActive: false,
          reassignManagerId: selectedHandoverManager.value
        };

        const res = await updateManager(userToDelete._id || userToDelete.id, payload);

        if (res.status) {
          fetchManagers();
          setShowDeleteModal(false);
          setUserToDelete(null);
          setSelectedHandoverManager(null);
        }
        setLoading(false);
      } else {
        // Normal toggle status
        const res = await toggleManagerStatus(userToDelete._id || userToDelete.id, !currentStatus);
        if (res.status) {
          fetchManagers();
          setShowDeleteModal(false);
          setUserToDelete(null);
        }
      }
    }
  };

  const handleClose = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
    setSelectedHandoverManager(null);
  };

  const totalPages = Math.ceil(totalRecords / rowsPerPage) || 1;
  const currentUsers = users;

  return (
    <>
      <div className="card h-100 p-0 radius-12">
        <div
          className="card-header border-bottom py-12 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between"
          style={{
            background: "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
          }}
        >
          <div className="d-flex align-items-center flex-wrap gap-3">
            <h6 className="text-white mb-0">Managers</h6>
          </div>

          <div className="d-flex align-items-center flex-wrap gap-3">
            <form
              className="navbar-search"
              onSubmit={(e) => e.preventDefault()}
            >
              <input
                type="text"
                className="form-control radius-8 ps-40 py-10 bg-white border-0"
                style={{ height: "44px" }}
                name="search"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearch}
              />
              <Icon icon="ion:search-outline" className="icon" style={{ color: "black" }} />
            </form>
            {hasPermission("managers", "add") && (
              <Link
                to="/employees/add"
                className="btn text-sm btn-sm px-12 py-8 radius-8 d-flex align-items-center gap-2"
                style={{ backgroundColor: "#f7e7da", color: "black", fontWeight: "600" }}
              >
                <Icon
                  icon="ic:baseline-plus"
                  className="icon text-xl line-height-1"
                />
                Add Manager
              </Link>
            )}
          </div>
        </div>
        <div className="card-body p-24">
          <div className="table-responsive border radius-8 bg-white overflow-x-auto shadow-sm">
            <table className="table bordered-table sm-table mb-0" style={{ minWidth: '1000px' }}>
              <thead>
                <tr style={{ backgroundColor: "#f7e7da" }}>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>S.No</th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Name</th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Mobile Number</th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Email</th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Role</th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Status</th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="7" className="text-center py-24">Loading...</td></tr>
                ) : currentUsers.length > 0 ? (
                  currentUsers.map((user, index) => (
                    <tr key={user._id || user.id}>
                      <td>{currentPage * rowsPerPage + index + 1}</td>
                      <td>
                        <div className="d-flex align-items-center gap-12 justify-content-start">
                          <div className="flex-shrink-0">
                            <div className="w-40-px h-40-px bg-primary-50 text-primary-600 d-flex justify-content-center align-items-center rounded-circle flex-shrink-0 overflow-hidden">
                              {user.profileImage ? (
                                <img
                                  src={getImageUrl(user.profileImage)}
                                  alt="Profile"
                                  className="w-100 h-100 object-fit-cover"
                                />
                              ) : (
                                <span className="text-lg fw-semibold">
                                  {user.name?.charAt(0)?.toUpperCase() || "A"}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                            {user.name}
                          </span>
                        </div>
                      </td>
                      <td>{user.phoneNumber || user.mobile}</td>
                      <td>
                        <span className="text-md mb-0 fw-normal text-secondary-light">
                          {user.email}
                        </span>
                      </td>
                      <td>{user?.role?.roleName || "N/A"}</td>

                      <td>
                        <span
                          className={`badge px-24 py-4 radius-4 fw-medium text-sm ${(user.isActive ?? user.status)
                            ? "bg-success-focus text-success-main"
                            : "bg-danger-focus text-danger-main"
                            }`}
                        >
                          {(user.isActive ?? user.status) ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-10">
                          {hasPermission("managers", "edit") && (
                            <Link
                              to={`/employees/edit/${user._id || user.id}`}
                              className="btn-edit-custom fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            >
                              <Icon
                                icon="lucide:edit"
                                className="menu-icon"
                              />
                            </Link>
                          )}
                          {hasPermission("managers", "delete") && (
                            <button
                              type="button"
                              onClick={() => confirmDelete(user)}
                              className={`remove-item-btn ${(user.isActive ?? user.status) ? 'bg-success-focus text-success-600' : 'bg-danger-focus text-danger-600'} fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0`}
                            >
                              <Icon icon={(user.isActive ?? user.status) ? "lucide:lock" : "lucide:unlock"} className="menu-icon" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-24">
                      No managers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            totalRecords={totalRecords}
          />
        </div>
      </div>

      <Modal show={showDeleteModal} onHide={handleClose} centered>
        <Modal.Body className="text-center p-5">
          <div className="d-flex justify-content-center mb-3">
            <div className={`${(userToDelete?.isActive ?? userToDelete?.status) ? "bg-success-focus" : "bg-danger-focus"} rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px`}>
              <Icon
                icon={(userToDelete?.isActive ?? userToDelete?.status) ? "lucide:unlock" : "lucide:lock"}
                className={`${(userToDelete?.isActive ?? userToDelete?.status) ? "text-success-600" : "text-danger-600"} text-xxl`}
              />
            </div>
          </div>
          <h5 className="mb-3">Are you sure?</h5>
          <p className="text-secondary-light mb-4">
            Do you want to {(userToDelete?.isActive ?? userToDelete?.status) ? "inactive" : "active"} manager "{userToDelete?.name}"?
          </p>
          {/* Handover Groups Section */}
          {(userToDelete?.isActive ?? userToDelete?.status) && getGroupsOfManager(userToDelete).length > 0 && (
            <div className="text-start mb-4 p-3 border rounded bg-light">
              <label className="form-label fw-bold text-dark d-block mb-2">
                This manager is assigned to {getGroupsOfManager(userToDelete).length} group(s). Hand over these groups to another active manager: <span className="text-danger">*</span>
              </label>
              {handoverLoading ? (
                <div className="text-muted small py-2">Loading active managers...</div>
              ) : activeManagersList.length === 0 ? (
                <div className="text-danger small py-2">No other active managers found. You must create or activate another manager first to perform a handover.</div>
              ) : (
                <Select
                  options={activeManagersList.map((m) => ({
                    value: m._id || m.id,
                    label: m.name,
                    original: m,
                  }))}
                  value={selectedHandoverManager}
                  onChange={(val) => setSelectedHandoverManager(val)}
                  styles={selectStyles()}
                  placeholder="Select Manager to Handover"
                  isClearable
                />
              )}
            </div>
          )}
          <div className="d-flex justify-content-center gap-3">
            <Button
              variant="outline-secondary"
              className="px-32"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              variant={(userToDelete?.isActive ?? userToDelete?.status) ? "danger" : "success"}
              className="px-32"
              onClick={handleDelete}
              disabled={(userToDelete?.isActive ?? userToDelete?.status) && getGroupsOfManager(userToDelete).length > 0 && !selectedHandoverManager}
              style={(userToDelete?.isActive ?? userToDelete?.status) ? { backgroundColor: "#ed1c24", borderColor: "#ed1c24" } : {}}
            >
              {(userToDelete?.isActive ?? userToDelete?.status) ? "Inactive" : "Active"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default EmployeeListLayer;
